/**
 * Authentication service API calls.
 * 
 * This module provides functions for user authentication including
 * registration, login, and user profile management.
 */

import apiClient from './api';

/**
 * Register a new user account
 * @param {Object} userData - User registration data
 * @param {string} userData.email - User's email address
 * @param {string} userData.password - User's password
 * @returns {Promise<Object>} Created user data
 */
export const registerUser = async (userData) => {
  try {
    const response = await apiClient.post('/api/auth/register', userData);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Registration failed'
    );
  }
};

/**
 * Login user and get JWT token
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - User's email address
 * @param {string} credentials.password - User's password
 * @returns {Promise<Object>} Login response with token
 */
export const loginUser = async (credentials) => {
  try {
    const response = await apiClient.post('/api/auth/login', credentials);
    
    // Store token in localStorage
    if (response.data.access_token) {
      localStorage.setItem('authToken', response.data.access_token);
    }
    
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Login failed'
    );
  }
};

/**
 * Get current user information
 * @returns {Promise<Object>} Current user data
 */
export const getCurrentUser = async () => {
  try {
    const response = await apiClient.get('/api/auth/me');
    
    // Store user data in localStorage
    localStorage.setItem('user', JSON.stringify(response.data));
    
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to get user information'
    );
  }
};

/**
 * Logout user (clear local storage)
 * @returns {Promise<Object>} Logout response
 */
export const logoutUser = async () => {
  try {
    const response = await apiClient.post('/api/auth/logout');
    
    // Clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    return response.data;
  } catch (error) {
    // Still clear localStorage even if API call fails
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    throw new Error(
      error.response?.data?.detail || 'Logout failed'
    );
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has valid token
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('authToken');
  const user = localStorage.getItem('user');
  
  return !!(token && user);
};

/**
 * Get stored user data
 * @returns {Object|null} User data or null if not found
 */
export const getStoredUser = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};

/**
 * Get stored auth token
 * @returns {string|null} Auth token or null if not found
 */
export const getStoredToken = () => {
  return localStorage.getItem('authToken');
};